import { useId, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { JOURNAL_PAGE_SIZE } from '@/lib/journal-aggregate';
import styles from './JournalPagination.module.css';

interface JournalPaginationProps {
  page: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

/**
 * Shared, bounded pagination for every accumulating Scripture Journal list.
 * Both controls stay mounted at the boundaries (disabled as appropriate), so
 * keyboard focus never falls back to <body> when the final page is reached.
 */
export default function JournalPagination({
  page,
  totalItems,
  onPageChange,
}: JournalPaginationProps) {
  const { t } = useTranslation();
  const statusId = useId();
  const previousRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const boundaryFocusRef = useRef<'previous' | 'next' | null>(null);
  const pageCount = Math.max(1, Math.ceil(totalItems / JOURNAL_PAGE_SIZE));
  const currentPage = Math.min(Math.max(0, page), pageCount - 1);

  // Native disabled buttons lose focus when a page turn makes the button the
  // new boundary. Move focus to the still-enabled counterpart after React has
  // committed, rather than letting it fall back to <body>.
  useLayoutEffect(() => {
    const target = boundaryFocusRef.current;
    if (!target) return;
    boundaryFocusRef.current = null;
    (target === 'previous' ? previousRef.current : nextRef.current)?.focus();
  }, [currentPage]);

  if (pageCount <= 1) return null;

  const start = currentPage * JOURNAL_PAGE_SIZE + 1;
  const end = Math.min(totalItems, start + JOURNAL_PAGE_SIZE - 1);

  return (
    <nav
      className={styles.pager}
      aria-label={t('bookmark.paginationLabel')}
      aria-describedby={statusId}
      data-journal-page={currentPage + 1}
      data-journal-range={`${start}-${end}`}
    >
      <button
        type="button"
        ref={previousRef}
        className={styles.button}
        disabled={currentPage === 0}
        onClick={() => {
          if (currentPage - 1 === 0) boundaryFocusRef.current = 'next';
          onPageChange(currentPage - 1);
        }}
      >
        {t('bookmark.previousPage')}
      </button>
      <span id={statusId} className={styles.status} role="status" aria-live="polite">
        {t('bookmark.pageStatus', {
          page: currentPage + 1,
          pages: pageCount,
          start,
          end,
          count: totalItems,
        })}
      </span>
      <button
        type="button"
        ref={nextRef}
        className={styles.button}
        disabled={currentPage >= pageCount - 1}
        onClick={() => {
          if (currentPage + 1 === pageCount - 1)
            boundaryFocusRef.current = 'previous';
          onPageChange(currentPage + 1);
        }}
      >
        {t('bookmark.nextPage')}
      </button>
    </nav>
  );
}
